import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { useAppSelector } from 'store';
import {
  DateFormatter,
  isCcmModule,
  isRpmModule,
} from 'infrastructure/functions';
import { DateFormats, ModuleType } from 'infrastructure/enums';

import type { SortState } from 'infrastructure/types';
import type {
  ISmsActivityResponse,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';

interface Props {
  patientGuid: string;
  meta: IPartialPaginationMeta;
  module?: ModuleType;
  documentationTimeSort: SortState;
  createdAtSort?: SortState;
}

type TSearchDatesMap = Record<
  ModuleType,
  {
    startTime?: string;
    endTime?: string;
    startDate?: string;
    endDate?: string;
  }
>;

type TFilter = {
  patientGuid: string;
  startTime?: string;
  endTime?: string;
  startDate?: string;
  endDate?: string;
  module: ModuleType;
  documentationTimeSort?: string;
  createdAtSort?: string;
} & IPartialPaginationMeta;

const getSearchDates = ([startTime, endTime]:
  | [Date, Date]
  | []): TSearchDatesMap => {
  return {
    [ModuleType.CCM]: {
      startTime: DateFormatter({
        date: startTime,
        format: DateFormats['MM-dd-yyyy'],
      }),
      endTime: DateFormatter({
        date: endTime,
        format: DateFormats['MM-dd-yyyy'],
      }),
    },
    [ModuleType.RPM]: {
      startDate: DateFormatter({
        date: startTime,
        format: DateFormats['yyyy-MM-dd HH:mm:s'],
      }),
      endDate: DateFormatter({
        date: endTime,
        format: DateFormats['MM-dd-yyyy'],
      }),
    },
    [ModuleType.RPMCCM]: {},
    [ModuleType.ADMIN]: {},
    [ModuleType.APCM]: {},
  };
};

export const getSmsActivitiesFn = async ({
  module,
  patientGuid,
  ...filter
}: TFilter) => {
  let url = 'core/ccm/sms-responses';
  if (isRpmModule(module)) url = `core/patients/${patientGuid}/messages`;

  const { data, error } = await dataService.getList<ISmsActivityResponse>(
    apiUrlGenerator(url, {
      ...filter,
      patientGuid: isCcmModule(module) ? patientGuid : undefined,
    }),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  if (data) {
    const [count, items] = Object.values(data);

    return { count, items } as ISmsActivityResponse;
  }
};

export const getSmsActivitiesKey = (filter: TFilter) => [
  'getSmsActivities',
  filter,
];

export const SMS_ACTIVITIES_PER_PAGE = 5;

export const useGetSmsActivities = ({
  patientGuid,
  meta,
  module = ModuleType.CCM,
  documentationTimeSort,
  createdAtSort,
}: Props) => {
  const dates = useAppSelector<[Date, Date] | []>(
    (state) => state.ccmPatientDetails.dates,
  );
  const [startTime, endTime] = dates;

  const filter: TFilter = {
    ...meta,
    patientGuid,
    items: SMS_ACTIVITIES_PER_PAGE,
    ...getSearchDates(dates)[module],
    module,
    documentationTimeSort: documentationTimeSort?.toUpperCase(),
    createdAtSort: createdAtSort?.toUpperCase(),
  };

  const {
    data: smsActivity,
    isLoading,
    isFetching,
    isRefetching,
    ...other
  } = useQuery({
    queryKey: getSmsActivitiesKey(filter),
    queryFn: async () => getSmsActivitiesFn({ ...filter }),
    enabled: Boolean(startTime) && Boolean(endTime),
  });

  return {
    smsActivity,
    isLoading: isLoading || isFetching || isRefetching,
    isFetching,
    ...other,
  };
};
